/**
 * Weekly-review commands (`docs/specs/phase-2c-weekly-review.md` WS3):
 * `acceptSuggestion` (habit-target realism, WR-9) and
 * `answerObjectiveCheckpoint` (WR-12). Shares the `CommandResult`/
 * `DomainCommandError` shape from `../event-log/command-error` with
 * `../habits/commands.ts` / `../objectives/commands.ts`; no I/O (ADR-0001
 * rule 2).
 *
 * `completeReview` and `acceptObjectiveDateSuggestion` are NOT here:
 * `completeReview` is simple enough (one event, no validation beyond "the
 * week must be available") to build directly in `create-domain-api.ts`
 * alongside its reward wiring, matching how that file already builds
 * `completeOnboarding` inline; `acceptObjectiveDateSuggestion` already exists
 * in `../objectives/commands.ts` (Phase 1) and is reused unchanged (WR-13).
 */

import type {
  AdjustmentSuggestionView,
  ClockPort,
  CompassEvent,
  HabitTargetAdjustedEvent,
  IdPort,
  ObjectiveCheckpointAnsweredEvent,
  ObjectiveCheckpointAnswer,
  ObjectiveId,
  WeekStart,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { buildHabitStates } from '../habits/state';
import { buildObjectiveStates } from '../objectives/state';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

/**
 * WR-9: records `habit.target_adjusted` with `source: 'review_suggestion'`
 * and `reviewWeek` set to the suggestion's own `reviewWeek` -- mirrors
 * `../habits/commands.ts`'s `adjustTargetManually` exactly except for the
 * `source`/`reviewWeek` discriminant (HB-4/WR-11: manual stays `'manual'`,
 * this path is the only producer of `'review_suggestion'`). The suggestion
 * itself is caller-supplied (from a prior `getWeeklyReview` read) rather
 * than recomputed here -- accepting exactly what was shown avoids a
 * TOCTOU mismatch between what the user saw and what gets recorded.
 */
export function acceptSuggestion(
  events: readonly CompassEvent[],
  suggestion: AdjustmentSuggestionView,
  ports: Ports,
): CommandResult<HabitTargetAdjustedEvent> {
  const states = buildHabitStates(events);
  const state = states.get(suggestion.habitId);
  if (!state || state.lifecycle === 'archived') {
    throw domainError('habit_not_found', `habit ${suggestion.habitId} not found`);
  }
  if (suggestion.adjustment.habitKind !== state.plan.habitKind) {
    throw domainError(
      'invalid_input',
      `suggestion habitKind (${suggestion.adjustment.habitKind}) must match the habit's fixed kind (${state.plan.habitKind})`,
    );
  }

  const event: HabitTargetAdjustedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'habit.target_adjusted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    habitId: suggestion.habitId,
    adjustment: suggestion.adjustment,
    source: 'review_suggestion',
    reviewWeek: suggestion.reviewWeek,
  };

  return { events: [event] };
}

/** WR-12: records the "progress / re-scope / on hold" checkpoint answer for one objective, one review week. */
export function answerObjectiveCheckpoint(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  weekStart: WeekStart,
  answer: ObjectiveCheckpointAnswer,
  ports: Ports,
): CommandResult<ObjectiveCheckpointAnsweredEvent> {
  const states = buildObjectiveStates(events);
  const state = states.get(objectiveId);
  if (!state || state.archived) {
    throw domainError('objective_not_found', `objective ${objectiveId} not found`);
  }

  const event: ObjectiveCheckpointAnsweredEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.checkpoint_answered',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    weekStart,
    answer,
  };

  return { events: [event] };
}
